import { useEffect, useRef } from "react";
import type { UseFormWatch } from "react-hook-form";

/** Field names shared by sender and careprovider login RHF forms. */
export type LoginFieldSideEffectsFormValues = {
  challenge: string;
  email: string;
  password: string;
};

function isLoginFieldName(
  name: string | undefined,
): name is keyof LoginFieldSideEffectsFormValues {
  return name === "email" || name === "password" || name === "challenge";
}

function isFieldEditEvent(type: string | undefined): boolean {
  return type === "change";
}

export type UseLoginFieldSideEffectsParams = {
  onChangeEmail?: (email: string) => void;
  resetError: () => void;
  watch: UseFormWatch<LoginFieldSideEffectsFormValues>;
};

/**
 * Mirrors legacy LoginPageV2 `sideMutation` / `onValueChange` behavior for RHF
 * forms: clear external login errors on edit and notify parent for SSO checks.
 *
 * Use in dsl login forms — not in ds/ui Controlled* components.
 */
export function useLoginFieldSideEffects({
  onChangeEmail,
  resetError,
  watch,
}: UseLoginFieldSideEffectsParams): void {
  const onChangeEmailRef = useRef(onChangeEmail);
  const resetErrorRef = useRef(resetError);
  onChangeEmailRef.current = onChangeEmail;
  resetErrorRef.current = resetError;

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (!isFieldEditEvent(type) || !isLoginFieldName(name)) {
        return;
      }

      resetErrorRef.current();

      if (name === "email") {
        onChangeEmailRef.current?.(values.email ?? "");
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);
}
